/**
 * FRONT-1601 FIX 3 — pure decision for whether the deep-research bar's
 * OPTIMISTIC flag should clear.
 *
 * `research-banner-logic.ts`'s `isResearchBannerVisible` shows the bar the
 * instant a deep-research plan is approved, before the real run row has
 * round-tripped back — that's the `optimistic` flag. The comment above it in
 * `inbox-chat-modal.tsx` always described it as self-clearing ("self-clears
 * the moment the real run is observed... or after a short timeout"), but
 * nothing in the code ever set it back to `false`. So the flag — and the
 * cobalt "In progress" bar it drives — stuck on for the rest of the session
 * once a plan was approved, long after the run it was standing in for had
 * actually finished. Re-opening that stale bar showed a blank panel (no run,
 * no counts, no strip, no controls), and it also broke
 * `research-finished-notice.ts`'s rule 2: a user could open the stale bar
 * and get a "Research finished" notice for a run they were never actually
 * watching.
 *
 * Two events clear it — either is sufficient:
 *
 * - The real run has round-tripped back (`runningResearchId` is no longer
 *   `null`). `isResearchBannerVisible` already ORs the real id in, so
 *   handing off costs no visible gap.
 * - `OPTIMISTIC_RESEARCH_WINDOW_MS` has elapsed with no run ever confirmed
 *   — a failed launch must not leave the bar on indefinitely.
 */
export function shouldClearOptimisticResearch(input: {
  /** The real research run id, or null if it hasn't arrived (or none is
   *  running). */
  runningResearchId: string | null;
  /** Whether the optimistic window has elapsed with no run ever confirmed. */
  windowElapsed: boolean;
}): boolean {
  return input.runningResearchId !== null || input.windowElapsed;
}
